import { Clipboard } from '@angular/cdk/clipboard';
import { ChangeDetectionStrategy, Component, inject, Input, input, OnInit, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { EnvService } from 'src/app/env.service';
import { UserGroupSearchComponent } from 'src/app/shared/components/user-group-search/user-group-search.component';
import { AppConfirmService } from 'src/app/shared/services/app-confirm/app-confirm.service';
import { OVERLAY_DATA } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { TenantService } from 'src/app/shared/services/tenant/tenant.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ChimeCallService } from '../chime-call.service';

export interface Invitation {
  type?: 'user' | 'group';
  entityId?: string;
  email?: string;
  name?: string;
  invitedAt: Date;
  joined: boolean;
  joinedAt?: Date;
}

@Component({
  selector: 'app-call-invitation',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, UserGroupSearchComponent],
  template: `
    <div class="invite-form-wrapper" [class.overlay-wrapper]="openedInOverlay">
      <!-- Add Participant and Share Link -->
      <div class="input-row d-flex gap-2 align-items-center">
        <app-user-group-search
          class="flex-grow-1"
          [searchControl]="emailFormControl"
          [placeholder]="'MediaRecorder.InviteParticipant' | translate"
          icon="person_add"
          iconPosition="prefix"
          (keydown.enter)="addParticipant()"
          (selectionChange)="onEntitySelected($event)"
        ></app-user-group-search>
        <button mat-icon-button color="primary" class="flex-shrink-0" (click)="copyInviteLink()" [matTooltip]="'MediaRecorder.ShareLink' | translate">
          <mat-icon>content_copy</mat-icon>
        </button>
      </div>

      <!-- Allowed Participants List -->
      <div class="participants-list-container mt-2">
        <div class="invite-section">
          <!-- Collapsible Header -->
          <div class="invite-section-header" [class.expanded]="listExpanded()" (click)="listExpanded.set(!listExpanded())">
            <span class="text-[12px] font-semibold tracking-wider"> {{ 'VideoCall.Lobby.AllowedParticipants' | translate }} ({{ invitations().length }}) </span>
            <div class="flex items-center">
              @if (listExpanded() && invitations().length > 0) {
                <button
                  mat-icon-button
                  color="primary"
                  class="send-all-btn flex-shrink-0"
                  (click)="sendInviteToAll($event)"
                  [matTooltip]="'VideoCall.Confirm.InviteAllTitle' | translate"
                  [disabled]="sendingAll()"
                >
                  @if (sendingAll()) {
                    <mat-spinner diameter="18"></mat-spinner>
                  } @else {
                    <mat-icon class="text-[18px]">send</mat-icon>
                  }
                </button>
              }
              <mat-icon class="toggle-icon flex-shrink-0" [class.expanded]="listExpanded()">chevron_right</mat-icon>
            </div>
          </div>

          <!-- Collapsible Body -->
          @if (listExpanded()) {
            <div class="participants-list custom-scrollbar">
              @if (loadingList()) {
                <div class="d-flex justify-content-center p-3">
                  <mat-spinner diameter="24"></mat-spinner>
                </div>
              } @else {
                @for (inv of invitations(); track inv.entityId || inv.email) {
                  <div class="invite-item">
                    <div class="flex items-center gap-2 flex-1 min-w-0">
                      @if (inv.type === 'group') {
                        <span class="text-[13px] font-medium text-[var(--skz-text-primary)] truncate" [matTooltip]="inv.name || inv.entityId">{{ inv.name || inv.entityId }}</span>
                        <span
                          class="px-2 py-0.5 rounded-full bg-[var(--skz-bg-primary-light)] text-[var(--skz-text-primary)] text-[10px] font-semibold border border-[var(--skz-border-color-main)] flex-shrink-0"
                          >{{ 'Global.Group' | translate }}</span
                        >
                      } @else {
                        <span class="text-[13px] font-medium text-[var(--skz-text-primary)] truncate" [matTooltip]="inv.email">{{ inv.email }}</span>
                        @if (inv.name && inv.name !== inv.email) {
                          <span
                            class="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-medium border border-slate-200 truncate max-w-[120px] flex-shrink-0"
                            [matTooltip]="inv.name"
                            >{{ inv.name }}</span
                          >
                        }
                      }
                    </div>
                    <div class="participant-actions flex items-center gap-1">
                      @if (inv.type !== 'group') {
                        <button
                          mat-icon-button
                          (click)="sendInvite(inv.email)"
                          [matTooltip]="'VideoCallDrawer.SendInvitation' | translate"
                          [disabled]="loadingEmail() === inv.email"
                        >
                          @if (loadingEmail() === inv.email) {
                            <mat-spinner diameter="15"></mat-spinner>
                          } @else {
                            <mat-icon class="text-[15px]">send</mat-icon>
                          }
                        </button>
                      }
                      <button mat-icon-button (click)="removeParticipant($event, inv)" [matTooltip]="'Global.Remove' | translate">
                        <mat-icon class="text-[15px]">Cancel</mat-icon>
                      </button>
                    </div>
                  </div>
                } @empty {
                  <div class="text-center py-3 text-[12px] text-[var(--skz-text-muted)]">
                    {{ 'Global.NoData' | translate }}
                  </div>
                }
              }
            </div>
          }
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./call-invitation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallInvitationComponent implements OnInit {
  @Input() openedInOverlay = false;
  @Input() callID: string;

  parentLoading = input(false);
  emailFormControl = new FormControl('', [Validators.required, Validators.email]);

  private clipboard = inject(Clipboard);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private chimeCallService = inject(ChimeCallService);
  private tenantService = inject(TenantService);
  private envService = inject(EnvService);
  private appConfirmService = inject(AppConfirmService);
  public data: {
    openedInOverlay: boolean;
    callID: string;
  } = inject(OVERLAY_DATA, { optional: true });

  public loading = signal(false);
  public loadingList = signal(false);
  public loadingEmail = signal<string | null>(null);
  public error = signal<string | null>(null);
  public invitations = signal<Invitation[]>([]);
  public listExpanded = signal(false);
  public sendingAll = signal(false);

  ngOnInit(): void {
    if (this.data) {
      this.openedInOverlay = this.data.openedInOverlay;
      this.callID = this.data.callID;
    }
    if (this.callID) {
      this.loadInvitations();
    }
  }

  copyInviteLink(): void {
    const tenantId = this.tenantService.activeTenantId() || this.envService.appName;
    const inviteLink = `${window.location.origin}/video-call/${this.callID}?tenantId=${encodeURIComponent(tenantId)}`;
    this.clipboard.copy(inviteLink);
    this.toast.snackbar.open(this.translate.instant('Snackbar.CopiedNotification'), 'Ok', { duration: 2000 });
  }

  async loadInvitations(): Promise<void> {
    this.loadingList.set(true);
    try {
      const res = await firstValueFrom(this.chimeCallService.getInvitations(this.callID));
      this.invitations.set(res.invitations || []);
    } catch (err) {
      console.error('Failed to load invitations:', err);
    } finally {
      this.loadingList.set(false);
    }
  }

  async addParticipant(): Promise<void> {
    if (!this.emailFormControl.valid || !this.emailFormControl.value) return;

    const email = this.emailFormControl.value;
    await this.addInvitationBackend(email, 'user');
  }

  async onEntitySelected(entity: any): Promise<void> {
    const type = entity.class === 'group' ? 'group' : 'user';
    const entityId = type === 'group' ? entity._id : entity.email || entity._id;
    await this.addInvitationBackend(entityId, type, entity.displayName);
  }

  private async addInvitationBackend(entityId: string, type: 'user' | 'group', name?: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      await firstValueFrom(this.chimeCallService.addInvitation(this.callID, entityId, type, name));
      this.emailFormControl.reset();
      await this.loadInvitations();
    } catch (err) {
      console.error('Failed to add invitation:', err);
      this.error.set('Failed to add invitation. Please try again.');
      this.toast.snackbar.open('Failed to add participant', 'Ok', { duration: 2000 });
    } finally {
      this.loading.set(false);
    }
  }

  async sendInvite(email: string): Promise<void> {
    this.loadingEmail.set(email);
    try {
      await firstValueFrom(this.chimeCallService.inviteToCall(this.callID, email));
      this.toast.snackbar.open(this.translate.instant('VideoCallDrawer.InvitationSent'), 'Ok', { duration: 2000 });
    } catch (err) {
      console.error('Failed to send invite:', err);
      this.toast.snackbar.open('Failed to send invite', 'Ok', { duration: 2000 });
    } finally {
      this.loadingEmail.set(null);
    }
  }

  async removeParticipant(event: Event, inv: Invitation): Promise<void> {
    try {
      this.loadingList.set(true);
      const entityId = inv.entityId || inv.email;
      if (entityId) {
        await firstValueFrom(this.chimeCallService.removeInvitation(this.callID, entityId, inv.type || 'user'));
        await this.loadInvitations();
      }
    } catch (err) {
      console.error('Failed to remove participant:', err);
      this.toast.snackbar.open('Failed to remove participant', 'Ok', { duration: 2000 });
    } finally {
      this.loadingList.set(false);
    }
  }

  async sendInviteToAll(event: Event): Promise<void> {
    event.stopPropagation();

    const title = this.translate.instant('VideoCall.Confirm.InviteAllTitle');
    const message = this.translate.instant('VideoCall.Confirm.InviteAllMessage');

    this.appConfirmService
      .confirm({
        title,
        message,
        yesText: this.translate.instant('Global.Send') || 'Send',
      })
      .subscribe(async (res) => {
        if (res === 1) {
          this.sendingAll.set(true);
          try {
            const emailsToInvite = new Set<string>();

            for (const inv of this.invitations()) {
              if (inv.type === 'group') {
                if (inv.entityId) {
                  try {
                    const group = await firstValueFrom(this.chimeCallService.getGroup(inv.entityId));
                    if (group && Array.isArray(group.people)) {
                      for (const person of group.people) {
                        if (person.email) {
                          emailsToInvite.add(person.email.trim().toLowerCase());
                        }
                      }
                    }
                  } catch (groupErr) {
                    console.error(`Failed to fetch group ${inv.entityId}:`, groupErr);
                  }
                }
              } else if (inv.email) {
                emailsToInvite.add(inv.email.trim().toLowerCase());
              }
            }

            const emailList = Array.from(emailsToInvite);
            if (emailList.length === 0) {
              this.toast.snackbar.open('No participants to invite.', 'Ok', { duration: 2000 });
              return;
            }

            await firstValueFrom(this.chimeCallService.inviteToCallBulk(this.callID, emailList));
            this.toast.snackbar.open(this.translate.instant('VideoCallDrawer.InvitationSent') || 'Invitations sent successfully.', 'Ok', { duration: 3000 });
            await this.loadInvitations();
          } catch (err) {
            console.error('Failed to send bulk invitations:', err);
            this.toast.snackbar.open('Failed to send invitations.', 'Ok', { duration: 2000 });
          } finally {
            this.sendingAll.set(false);
          }
        }
      });
  }
}
